import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useOnboardingGuide } from '@/contexts/OnboardingGuideContext';
import { useDashboardMode } from '@/hooks/useDashboardMode';
import { MonoLabel } from '@/components/editorial';
import {
  SidebarBrand,
  SidebarProfileRow,
  SidebarRoleSwitcher,
  SidebarFooter,
  NavItemWithTooltip,
  type NavSection,
} from './sidebar-primitives';
import { buildParentNavSections, MENU_KEY_MAP, SIDEBAR_FOOTER_ITEMS } from './parent-nav';
import { ResizeHandle } from './ResizeHandle';
import { SIDEBAR_WIDTH } from '@/lib/layout-prefs';
import { buildAdminNavSections } from './admin-nav';
import { useDrawerInert } from './useIsMdScreen';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  collapsed?: boolean;
  pinned?: boolean;
  /** Collapse / expand the pinned rail — the brand block's panel icon (2026-09-20). */
  onToggle?: () => void;
  /** Which edge the panel anchors to. 'right' improves one-handed phone reach. */
  side?: 'left' | 'right';
  /** Opens the profile dialog — the profile row is the only entry point (#768). */
  onProfileClick?: () => void;
  /** Current pinned width in px (#1705 item 7); the handle renders only when both are given. */
  width?: number;
  onWidthChange?: (px: number) => void;
}

export function Sidebar({
  isOpen,
  onClose,
  collapsed = false,
  pinned = true,
  onToggle,
  side = 'left',
  onProfileClick,
  width,
  onWidthChange,
}: SidebarProps) {
  const { user, impersonation } = useAuth();
  const { guideStep } = useOnboardingGuide();
  // Re-renders on a language change, so the nav rows (whose labels resolve
  // through `NAV_KEY` when read, #2109 L6) follow the locale.
  const { t } = useTranslation('shell');
  // #2595: closed and not a visible rail → out of the tab order.
  const inert = useDrawerInert(isOpen, pinned);

  // When the anchored side flips, the *closed* drawer's transform sign inverts
  // (-translate-x-full ↔ translate-x-full). With the slide transition active
  // that animates the panel from one off-screen edge to the other, sweeping it
  // straight through the visible center for a frame. Drop the transition for
  // the single commit in which `side` changes so the panel simply reappears
  // off-screen on the new edge. useLayoutEffect (not useEffect) is required:
  // the transition must be removed before the browser paints, or the sweep has
  // already rendered.
  const [suppressAnim, setSuppressAnim] = useState(false);
  const prevSide = useRef(side);
  useLayoutEffect(() => {
    if (prevSide.current !== side) {
      prevSide.current = side;
      setSuppressAnim(true);
    }
  }, [side]);
  useEffect(() => {
    if (!suppressAnim) return;
    const raf = requestAnimationFrame(() => setSuppressAnim(false));
    return () => cancelAnimationFrame(raf);
  }, [suppressAnim]);

  // Escape closes the drawer (#2435). It is a hand-built overlay, not a
  // Radix dialog, so nothing else gives it the key.
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  const isAdmin = user?.role === 'admin';
  const canReviewApplications = user?.permissions?.can_review_applications === true;

  // #693 memorial gate on the Health entry. The hook no-ops for non-parents
  // (its query is `enabled: role === 'parent'`), so the admin branch below is
  // unaffected. Withheld while the family record is in flight as well as in
  // memorial mode: the fail-safe mode is 'active', so rendering the item first
  // would show a bereaved parent the offer and then take it away.
  const { isMemorial, isLoading: modeLoading } = useDashboardMode();
  const showHealth = !modeLoading && !isMemorial;

  // Admin gets the grouped admin sections. Parents get their own grouped
  // sections. Trfers render through TRFerSidebar (a separate component
  // selected by the layout), not this file — Sidebar handles parent + admin
  // only. Impersonation filters parent items down to allowed menus —
  // sections that empty out are hidden along with their labels. Admin
  // impersonation isn't a thing yet, so admin sections always render whole.
  const sections: NavSection[] = isAdmin
    ? buildAdminNavSections({ canReviewApplications })
    : impersonation
      ? buildParentNavSections({ showHealth })
          .map((s) => ({
            ...s,
            items: s.items.filter((item) => {
              const key = MENU_KEY_MAP[item.href];
              return !key || impersonation.allowedMenus.includes(key);
            }),
          }))
          .filter((s) => s.items.length > 0)
      : buildParentNavSections({ showHealth });

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className={cn('fixed inset-0 bg-stevens-scrim-soft z-[60]', pinned && 'md:hidden')}
          onClick={onClose}
        />
      )}

      {/* Sidebar panel. `inert` while closed off-canvas (#2595): the slide
          hides its paint, not its 140-odd focusable controls. */}
      <aside
        inert={inert}
        className={cn(
          // As a drawer it is a full-height overlay ABOVE the z-50 header
          // (#2435, the shell stacking contract of D-2398). It used to start
          // at top-12, under a header assumed to begin at y 0; the language /
          // machine-translation row above the header pushed the header down
          // over the drawer's brand row and close button, and left the
          // header's buttons live above the scrim. The brand row carries the
          // mark and the close button, so covering the header loses nothing.
          'fixed top-0 z-[60] h-[100dvh] pt-[env(safe-area-inset-top)] bg-stevens-bg-2 border-stevens-rule-2 flex flex-col',
          // No transition for the one commit where the side flips (else the
          // closed drawer sweeps across the screen); restored next frame.
          !suppressAnim && 'transition-all duration-300',
          // Anchor + divider edge mirror the chosen side.
          side === 'right' ? 'right-0 border-l' : 'left-0 border-r',
          // Pinned at md+ it is a rail beside the content, back at z-40 so
          // every z-50 dialog and its scrim still cover it.
          pinned && 'md:z-40 md:pt-0',
          // Full height beside the content, impersonating or not: the
          // impersonation banner is a row of the content column now (#2937),
          // not a fixed strip the rail had to start under.
          pinned && 'md:top-0 md:h-screen',
          // Expanded width is the user's (#1705 item 7), default 16rem.
          collapsed ? 'w-16' : 'w-[var(--trf-sidebar-w,16rem)]',
          // Closed drawer slides off its own edge.
          isOpen ? 'translate-x-0' : side === 'right' ? 'translate-x-full' : '-translate-x-full',
          pinned && 'md:translate-x-0',
        )}
      >
        {/* Brand block — TRF mark + Tyler Robinson Foundation for every role
            (#872). The patient identity block that used to replace it for
            parents is gone (#844): a caregiver does not need reminding of
            their own child's name and age, and its cycle label + incrementing
            age rendered ungated for bereaved families. The case-manager line
            it carried lives on /circle now. */}
        <SidebarBrand collapsed={collapsed} pinned={pinned} onToggle={onToggle} onClose={onClose} />

        {/* Drag handle on the inner edge (#1705 item 7) — pinned, expanded, md+ only. */}
        {pinned && !collapsed && width != null && onWidthChange && (
          <ResizeHandle
            value={width}
            min={SIDEBAR_WIDTH.min}
            max={SIDEBAR_WIDTH.max}
            onChange={onWidthChange}
            label={t('sidebar.resizeTarget')}
            direction={side === 'right' ? 'shrink' : 'grow'}
            className={cn('absolute top-0 bottom-0 w-2 hidden md:flex', side === 'right' ? 'left-0' : 'right-0')}
          />
        )}

        {/* Navigation — fills available space, scrolls if it doesn't fit. */}
        <nav className="flex flex-col gap-1 p-2 flex-1 min-h-0 overflow-y-auto">
          {sections.map((section, idx) => (
            <div
              key={section.label ?? `utility-${idx}`}
              className={cn(
                'rounded-lg border bg-card/40',
                idx > 0 && 'mt-3',
                collapsed ? 'border-border/40 p-1' : 'border-border/60 p-2',
              )}
            >
              {section.label && !collapsed && (
                <MonoLabel className="block px-1 pb-1.5 mb-1 border-b border-border/40 text-stevens-ink-3/70">
                  {section.label}
                </MonoLabel>
              )}
              <div className="flex flex-col gap-1">
                {section.items.map((item) => (
                  <NavItemWithTooltip
                    key={item.name}
                    href={item.href}
                    icon={item.icon}
                    name={item.name}
                    collapsed={collapsed}
                    onClose={onClose}
                    end={item.end}
                    highlight={guideStep === 'tasks-nav' && item.href === '/tasks'}
                  />
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* Footer area — Settings, Help, profile row, role switcher. Sits at
            the bottom of the flex column naturally — no absolute positioning
            (that previously hid the last nav items behind the footer when the
            nav outgrew the visible sidebar height, mirrors TRFerSidebar).

            The role switcher renders BELOW the profile row (#844) but stays
            its SIBLING, never its child: it was deliberately lifted out of
            that row in #768, and folding it back in would silently delete
            role switching for every multi-role account on the parent/admin
            side. Its dropdown already opens side='top', so the bottom slot is
            safe. Legal links moved to `LegalStrip`. */}
        <div className="border-t bg-background">
          {/* Settings · Help, from `SIDEBAR_FOOTER_ITEMS` so the parity test
              sees the same tray the reader does. No separate Notifications
              item — it's a tab inside Settings now, and a second /settings
              NavLink would double-highlight. */}
          <div className="px-2 pt-2 flex flex-col gap-1">
            {SIDEBAR_FOOTER_ITEMS.map((item) => (
              <NavItemWithTooltip
                key={item.href}
                href={item.href}
                icon={item.icon}
                name={item.name}
                collapsed={collapsed}
                onClose={onClose}
                end={item.end}
              />
            ))}
          </div>

          <div className="px-2 pb-2 pt-1.5 flex flex-col gap-1">
            <SidebarProfileRow collapsed={collapsed} onProfileClick={onProfileClick} />
            <SidebarRoleSwitcher collapsed={collapsed} />
          </div>

          {/* Parent tagline retired (#844) — enrollment marketing aimed at a
              family that is already enrolled. The admin line ("Steady hands
              behind the platform.") went on 2026-09-18 for the same rule as
              the TRFer one: a sentence in the chrome is deleted
              (ui-ux-guideline §2.8 rule 6). The trf.org link stays. */}
          {isAdmin && <SidebarFooter collapsed={collapsed} />}
        </div>
      </aside>
    </>
  );
}
